import Button from "components/button";
import { useSelector } from "react-redux";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { ORDER_TYPE } from "constants";
import { FiDownload } from "react-icons/fi";

/* ------------ Small helpers (simple & readable) ------------ */
const show = (v) =>
  v === null || v === undefined || v === "" ? "—" : String(v);
const toIST = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("en-IN", { timeZone: "Asia/Kolkata" });
};
const isCarryingType = (t) =>
  String(t || "").toLowerCase() ===
  String(ORDER_TYPE?.CARRYING || "CARRYING").toLowerCase();

/* ------------ Draw the header/meta block ------------ */
function drawOrderHeader(doc, order) {
  const pageWidth = doc.internal.pageSize.getWidth();

  // Background gradient effect (using rectangles with decreasing opacity)
  doc.setFillColor(51, 17, 219); // Blue-500
  doc.rect(0, 0, pageWidth, 15, "F");

  // Main title with white text
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont(undefined, "bold");
  doc.text("ORDER REPORT", pageWidth / 2, 10, { align: "center" });

  // Reset text color for content
  doc.setTextColor(0, 0, 0);

  // Content area with subtle shadow effect
  const contentY = 20;
  doc.setFillColor(248, 250, 252); // Gray-50
  doc.roundedRect(10, contentY, pageWidth - 20, 25, 3, 3, "F");

  // Add subtle border
  doc.setDrawColor(226, 232, 240); // Gray-200
  doc.setLineWidth(0.5);
  doc.roundedRect(10, contentY, pageWidth - 20, 25, 3, 3, "S");

  // Two columns of details with modern styling
  const leftX = 20;
  const rightX = pageWidth / 2 + 10;
  const startY = contentY + 6;
  const gapY = 5;

  const left = [
    ["Order No", show(order?.orderNo)],
    ["Status", `${show(order?.status?.name)} `],
    ["Order Type", show(order?.orderType)],
    ["Shipment", show(order?.shipmentType)],
  ];
  const right = [
    ["Created By", show(order?.createdByUser)],
    ["Order For", show(order?.orderForUser)],
    ["Created At", toIST(order?.createdAt)],
    ["Updated At", toIST(order?.updatedAt)],
  ];

  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  left.forEach(([label, value], i) => {
    const y = startY + i * gapY;
    doc.text(`${label}:`, leftX, y);
    doc.setTextColor(0, 0, 0);
    doc.text(String(value), leftX + 30, y);
    doc.setTextColor(100, 100, 100);
  });

  right.forEach(([label, value], i) => {
    const y = startY + i * gapY;
    doc.text(`${label}:`, rightX, y);
    doc.setTextColor(0, 0, 0);
    doc.text(String(value), rightX + 30, y);
    doc.setTextColor(100, 100, 100);
  });

  // Where to continue after columns
  let nextY = startY + Math.max(left.length, right.length) * gapY + 5;

  // Reset color
  doc.setTextColor(0, 0, 0);
  return nextY; // return Y position to start the table
}

const ExportPdfButton = () => {
  const { orderDetailsData, orderDetailsLoading, orderDetailsError } =
    useSelector((state) => state.orders);

  const isCarrying = isCarryingType(orderDetailsData?.orderType);

  // Table headers (based on order type)
  const getHead = () => {
    if (isCarrying) {
      return [
        [
          "Item Name",
          "Description",
          "Marka",
          "CTN",
          "QTY/CTN",
          "KG/CTN",
          "CBM/CTN",
          "Rate",
          "Total Amount",
          "Total QTY",
          "Total KG",
          "Total CBM",
          "Container",
        ],
      ];
    }
    return [["Item Name", "Description", "QTY", "Price", "Container"]];
  };

  // Table row mapping (based on order type)
  const mapItem = (item) => {
    if (isCarrying) {
      return [
        show(item?.name),
        show(item?.description),
        show(item?.marka),
        String(item?.ctn ?? 0),
        String(item?.qty ?? 0),
        String(item?.kg ?? 0),
        String(item?.cbm ?? 0),
        String(item?.rate ?? 0),
        String(item?.totalAmount ?? 0),
        String(item?.totalQty ?? 0),
        String(item?.totalKg ?? 0),
        String(item?.totalCBM ?? 0),
        show(item?.container?.containerNo || item?.container?.name),
      ];
    }
    return [
      show(item?.name),
      show(item?.description),
      String(item?.totalQty ?? item?.qty ?? 0),
      String(item?.price ?? 0),
      show(item?.container?.containerNo || item?.container?.name),
    ];
  };

  const handleExportPDF = () => {
    if (orderDetailsError) {
      console.error("❌ Cannot export: order details error");
      return;
    }
    if (!orderDetailsData || !orderDetailsData.items?.length) {
      console.warn("⚠️ No order items to export");
      return;
    }

    const doc = new jsPDF({ orientation: "landscape" });

    // Header/meta block
    const tableStartY = drawOrderHeader(doc, orderDetailsData);

    // Table
    autoTable(doc, {
      head: getHead(),
      body: orderDetailsData.items.map(mapItem),
      startY: tableStartY,
      styles: { fontSize: 8, cellPadding: 2, overflow: "linebreak" },
      headStyles: { fillColor: [51, 17, 219], textColor: 255 },
    });

    doc.save(`Order-${orderDetailsData.orderNo || "report"}.pdf`);
  };

  return (
    <Button onClick={handleExportPDF} disabled={orderDetailsLoading}>
      <FiDownload className="mr-2 inline-block" />
      Export
    </Button>
  );
};

export default ExportPdfButton;
